import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { ChevronIcon, IconButton } from '@keplerco/core';
import styles from './horizontal-scroller.module.css';
import classNames from 'classnames';

interface IHorizontalScroller {
  scrollItemsLength: number;
  width?: string;
}

export function HorizontalScroller({ scrollItemsLength, width, children }: PropsWithChildren<IHorizontalScroller>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  function handleScroll() {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    function checkIfScrollable() {
      if (!scrollContainer) return;
      const isContentScrollable = scrollContainer.scrollWidth > scrollContainer.clientWidth;
      setIsScrollable(isContentScrollable);

      if (isContentScrollable) handleScroll();
      else {
        setCanScrollLeft(false);
        setCanScrollRight(false);
      }
    }

    if (!!scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', checkIfScrollable);

      checkIfScrollable();

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', checkIfScrollable);
      };
    }
  }, [scrollItemsLength]);

  function scrollLeft() {
    if (scrollContainerRef.current && canScrollLeft) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  }

  function scrollRight() {
    if (scrollContainerRef.current && canScrollRight) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  }
  return (
    <div className={styles.scrollableContainer} style={{ width: !!width ? width : 'calc(100% - 20px)' }}>
      <IconButton className={classNames({ invisible: !isScrollable })} iconType='stroke' onClick={scrollLeft} disabled={!canScrollLeft} aria-label="Scroll left" aria-hidden={!isScrollable}>
        <ChevronIcon rotation={3} strokeWidth={2} tone={canScrollLeft ? 'secondary' : 'default'} />
      </IconButton>

      <div ref={scrollContainerRef} className={styles.container} style={{ justifyContent: `${!!width ? 'space-between' : 'flex-start'}` }} onScroll={handleScroll}>
        {children}
      </div>

      <IconButton className={classNames({ invisible: !isScrollable })} iconType='stroke' onClick={scrollRight} disabled={!canScrollRight} aria-label="Scroll right" aria-hidden={!isScrollable}>
        <ChevronIcon rotation={1} strokeWidth={2} tone={canScrollRight ? 'secondary' : 'default'} />
      </IconButton>
    </div >
  );
}

